/**
 * User Repository
 * 
 * Handles data access for app_users table.
 * Provides CRUD operations for user management.
 */

import { BaseRepository } from './BaseRepository';

export interface AppUser {
  id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'user' | 'readonly';
  permissions: any;
  phone_agent?: boolean;
  created_at: string;
  updated_at: string;
}

export type AppUserUpdate = Partial<Omit<AppUser, 'id' | 'created_at' | 'email'>>;

export class UserRepository extends BaseRepository<AppUser> {
  constructor() {
    super({ tableName: 'app_users', primaryKey: 'id' });
  }

  /**
   * Get all users ordered by creation date (newest first)
   */
  async getAllUsers(): Promise<AppUser[]> {
    const { data, error } = await this.client
      .from(this.getTableName())
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch users: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Update a user's details
   * 
   * @param userId - The user ID to update
   * @param updates - Partial user data to update
   */
  async updateUser(userId: string, updates: AppUserUpdate): Promise<AppUser> {
    const { data, error} = await this.client
      .from(this.getTableName())
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user: ${error.message}`);
    }

    if (!data) {
      throw new Error('No user data returned after update');
    }

    return data;
  }

  /**
   * Delete a user from the database
   * Note: This only deletes from app_users. Auth deletion must be handled separately.
   * 
   * @param userId - The user ID to delete
   */
  async deleteUser(userId: string): Promise<void> {
    const { error } = await this.client
      .from(this.getTableName())
      .delete()
      .eq('id', userId);

    if (error) {
      throw new Error(`Failed to delete user: ${error.message}`);
    }
  }
}
